import React, { useEffect } from 'react'
import { AiFillHeart, AiOutlineShoppingCart } from 'react-icons/ai'
import { FaEye } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import Ratings from '../Ratings'
import { add_to_card, messageClear, add_to_wishlist, setPendingCartItem } from '../../store/reducers/cardReducer'
import { saveProductToSession, getGuestSessionId } from '../../utils/cartUtils'
import { getProxiedUrl } from '../../utils/imageUtils'
import { getDisplayPrice } from '../../utils/priceCalculator'

const FeatureProducts = ({ products, sellerId, sellerName }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { userInfo } = useSelector(state => state.auth)
    const { successMessage, errorMessage } = useSelector(state => state.card)

    // Filter products to show only those with pack size 1
    const filteredProducts = products.filter(product => {
        return product.packSize === 1;
    });

    // If no products with packSize === 1, show regular products (fallback)
    const displayProducts = filteredProducts.length > 0 ? filteredProducts : products;

    const add_card = (p) => {
      if (userInfo) {
          dispatch(add_to_card({
              userId: userInfo.id,
              quantity: 1,
              productId: p._id,
              price: p.price,
              sellerId: sellerId,
              packSize: p.packSize,
              selectedImage: p.images?.[0] || "",
          }))
      } else {
          // For guest users, add to cart directly
          dispatch(add_to_card({
              userId: null,
              quantity: 1,
              productId: p._id,
              price: p.price,
              sellerId: sellerId,
              packSize: p.packSize,
              selectedImage: p.images?.[0] || "",
          }))
      }
  }

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage)
            dispatch(messageClear())
        }
        if (errorMessage) {
            toast.error(errorMessage)
            dispatch(messageClear())
        }
    }, [errorMessage, successMessage])

    const add_wishlist = (pro) => {
        if (userInfo) {
            dispatch(add_to_wishlist({
                userId: userInfo.id,
                productId: pro._id,
                name: pro.name,
                price: pro.price,
                image: pro.images[0],
                discount: pro.discount,
                rating: pro.rating,
                slug: pro.slug,
                currency: pro.currency
            }))
        } else {
            // For guest users, add to wishlist using sessionId
            const sessionId = getGuestSessionId();
            
            dispatch(add_to_wishlist({
                sessionId: sessionId,
                productId: pro._id,
                name: pro.name,
                price: pro.price,
                image: pro.images[0],
                discount: pro.discount,
                rating: pro.rating,
                slug: pro.slug,
                currency: pro.currency
            }))
        }
    }
    return (
      <div className="w-[85%] flex flex-wrap mx-auto">
        <div className="w-full">
          <div className="text-center flex justify-center items-center flex-col text-4xl text-slate-600 font-bold relative pb-[45px]">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Featured Products</h2>
            <p className="text-gray-600 text-lg mb-4">Handpicked products just for you</p>
            <div className="w-[100px] h-[4px] bg-[#7fad39] mt-4"></div>
          </div>
        </div>
        <div className="w-full grid grid-cols-3 md-lg:grid-cols-2 md:grid-cols-1 gap-6">
          {displayProducts.map((p, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-gray-100 group transition-all duration-300 hover:shadow-lg hover:-mt-2"
            >
              <div className="relative w-[180px] h-[140px] mx-auto overflow-hidden rounded-lg">
                <Link to={`/${sellerName}/product/details/${p.slug}`} className="block h-full">
                  <img
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                    src={getProxiedUrl(p.images[0])}
                    alt={p.name}
                  />
                </Link>
                {p.discount ? (
                  <div className="flex justify-center items-center absolute text-white w-[38px] h-[38px] rounded-full bg-red-500 font-semibold text-xs left-2 top-2 z-10 shadow-md">
                    -{p.discount}%
                  </div>
                ) : (
                  ""
                )}

                <ul className="flex transition-all duration-300 -bottom-10 justify-center items-center gap-2 absolute w-full group-hover:bottom-3 z-10">
                  <li
                    onClick={() => add_wishlist(p)}
                    className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-indigo-600 hover:text-white hover:rotate-[720deg] transition-all duration-300 shadow-md"
                  >
                    <AiFillHeart />
                  </li>
                  <Link
                    to={`/${sellerName}/product/details/${p.slug}`}
                    className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-indigo-600 hover:text-white hover:rotate-[720deg] transition-all duration-300 shadow-md"
                  >
                    <FaEye />
                  </Link>
                  <li
                    onClick={() => add_card(p)}
                    className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-indigo-600 hover:text-white hover:rotate-[720deg] transition-all duration-300 shadow-md"
                  >
                    <AiOutlineShoppingCart />
                  </li>
                </ul>
              </div>
              <div className="p-4">
                <Link to={`/${sellerName}/product/details/${p.slug}`}>
                  <h2 className="text-gray-800 font-medium mb-2 hover:text-indigo-600 transition-colors duration-200 line-clamp-2">{p.name}</h2>
                </Link>
                <div className="flex justify-between items-center">
                  <div className="flex flex-col gap-0.5">
                    {(() => {
                      const display = getDisplayPrice(p);
                      return (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-indigo-600">
                              {display.currency}
                              {Number(display.price).toFixed(2)}
                              {display.isBulk && (
                                <span className="text-sm font-normal text-gray-600 ml-1">for {display.quantity}</span>
                              )}
                            </span>
                            {!display.isBulk && p.discount > 0 && (
                              <span className="text-sm text-gray-500 line-through">
                                {p.currency}{Number(p.price).toFixed(2)}
                              </span>
                            )}
                          </div>
                          {display.isBulk && (
                            <span className="text-sm text-gray-500">
                              {display.currency}{Number(display.perItemPrice).toFixed(2)} per item
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex">
                    <Ratings ratings={p.rating} />
                  </div>
                </div>
                {(p.showStockQuantity !== false || p.stock <= 0) && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`w-2 h-2 rounded-full ${p.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    <span className="text-sm text-gray-600">
                      {p.stock > 0 ? (p.showStockQuantity ? `${p.stock} in stock` : 'In stock') : 'Out of stock'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
}

export default FeatureProducts